import { useForm, yupResolver } from "@mantine/form"
import { useLocalStorage } from "@mantine/hooks"
import { CheckCircle } from "@phosphor-icons/react"
import {
  Breadcrumbs,
  Button,
  Group,
  SpinnerOverlay,
  Toast,
} from "@vesatogo/grass-core"
import "@vesatogo/grass-dates/index.css"
import { inr } from "@vesatogo/utils"
import dayjs from "dayjs"
import { cloneDeep, debounce, get, set } from "lodash-es"
import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useAuthorization } from "~/components/AuthorizationProvider"
import { FormCard } from "~/components/FormCard"
import HowIsItTraded from "~/components/HowIsItTraded"
import { OrderPattiRefs } from "~/components/ProductionPlanning/OrderPattiRefs"
import { ProductionParticulars } from "~/components/ProductionPlanning/ProductionParticulars"
import { WhenIsItProduction } from "~/components/ProductionPlanning/WhenIsItProduction"
import { WhoOverseesProduction } from "~/components/ProductionPlanning/WhoOverseesProduction"
import TermsAndConditions from "~/components/TermsAndConditions"
import TradeExpenses from "~/components/TradeExpenses"
import { WhoIsTrading } from "~/components/WhoIsTrading"
import { Permission } from "~/constants/Permissions"
import { PRODUCTION_TRADE_STATUS } from "~/constants/ProductionTradeStatus"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import {
  useAddUpdateProductionFinalTradeMutation,
  useAddUpdateProductionPlanningTradeMutation,
  useAddUpdateSharedEntityMutation,
  useAllDepartmentsQuery,
  useProductionTradeDetailQuery,
} from "~/generated/graphql"
import { productionPlanningValidationSchema } from "~/models/productionPlanning"
import {
  expenseAmount,
  expenseAmountBuyer,
  normalizeExpenses,
} from "~/utils/expenseAmount"

type SharedEntity = {
  group: SharedEntityGroup
}

export const ProductionPlanningDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const [, upsertSharedEntity] = useAddUpdateSharedEntityMutation()
  const [, addUpdateProductionPlanning] =
    useAddUpdateProductionPlanningTradeMutation()
  const [, addUpdateFinalPatti] = useAddUpdateProductionFinalTradeMutation()
  const [dept] = useLocalStorage({ key: "department" })
  const [{ data: alldepts }] = useAllDepartmentsQuery()

  const currentDept = alldepts?.department?.find(
    dep => dep?.id?.toString() === dept?.toString()
  )

  const form = useForm<any>({
    initialValues: {
      mode: "direct",
      patti_type: null,
      buyer: null,
      production_officer: null,
      production_depot: null,
      start_date_time: dayjs(),
      end_date_time: dayjs(),
      output_items: [{}],
      input_items: [{}],
      packaging_items: [{}],
      order_trade_ref_ids: [],
      expenses: [{}],
      seller_tnc: "",
      buyer_tnc: "",
      standard_deduction: 0,
    },
    validate: yupResolver(productionPlanningValidationSchema),
  })

  const { values: state, setValues: setState, getInputProps } = form
  const [{ data, fetching: detailFetching }] = useProductionTradeDetailQuery({
    variables: {
      id: Number(id),
    },
    pause: !id,
    requestPolicy: "network-only",
  })

  const isNewFinal = searchParams.get("final") === "true"
  const isFinal = isNewFinal || data?.production_trade_by_pk?.kind === "final"
  const canEditFinalPatti =
    isFinal && useAuthorization(Permission.CanEditProductionFinalPatti)

  const isCancelled =
    !isNewFinal &&
    data &&
    data?.production_trade_by_pk?.status ===
      PRODUCTION_TRADE_STATUS.CANCELLED?.id

  const isCompletelyDisabled =
    (isFinal && !isNewFinal && !canEditFinalPatti) ||
    (!isNewFinal && Boolean(data?.production_trade_by_pk?.id))
  const [initialized, setInitialized] = useState(false)

  const runComputations = useMemo(
    () =>
      debounce(values => {
        const stateCopy = cloneDeep(values)
        let totalQty = 0
        let totalAmount = 0
        let totalExpenseAmount = 0
        let buyerExpenseAmount = 0

        stateCopy.output_items?.forEach((group: any) => {
          group.details?.forEach((detail: any) => {
            const qty = Number(detail.qty || 0)
            const costPerUnit = Number(detail.cost_per_unit || 0)
            const itemTotalAmount = qty * costPerUnit
            // detail.total_cost = itemTotalAmount
            totalAmount += itemTotalAmount
            totalQty += qty
          })
        })

        stateCopy.total_quantity = totalQty
        stateCopy.total_amount = totalAmount

        stateCopy.amount_deducted =
          stateCopy.total_amount *
          (Number(stateCopy.standard_deduction || 0) / 100)

        stateCopy.quantity_deducted =
          stateCopy.total_quantity *
          (Number(stateCopy.standard_deduction || 0) / 100)

        for (let j = 0; j < stateCopy.expenses.length; j++) {
          const expense = stateCopy.expenses[j]
          const expenseParams = {
            kind: expense?.kind?.id,
            buyerAmount: Number(expense.buyer?.value) || 0,
            sellerAmount: Number(expense.seller?.value) || 0,
            facilitatorAmount: Number(expense.facilitator?.value) || 0,
            finalAmount: stateCopy.total_amount,
            finalQuantity:
              stateCopy.total_quantity - stateCopy.quantity_deducted,
            totalQuantity: stateCopy.total_quantity,
          }
          expense.amount = expenseAmount(expenseParams)
          totalExpenseAmount += expense.amount
          expense.buyer_expense = expenseAmountBuyer(expenseParams)
          buyerExpenseAmount += expense.buyer_expense
        }

        stateCopy.expense_amount = totalExpenseAmount
        stateCopy.buyer_expense = buyerExpenseAmount
        stateCopy.buyer_amount =
          stateCopy.total_amount -
          stateCopy.amount_deducted +
          stateCopy.buyer_expense

        form.setValues(stateCopy)
      }, 500),
    []
  )

  useEffect(() => {
    const productionTrade = data?.production_trade_by_pk
    const groupItems = (
      items: any[],
      type: "input" | "output" | "packaging"
    ) => {
      return (
        items?.reduce((acc: any[], item: any) => {
          const commodityId = item.commodity?.id
          const varietyId = item.variety?.id
          let group = acc.find(
            g =>
              g.commodity?.shared_id === commodityId &&
              g.variety?.shared_id === varietyId
          )
          if (!group) {
            group = {
              commodity: item.commodity?.commodity?.[0]
                ? { ...item.commodity.commodity[0], shared_id: commodityId }
                : undefined,
              variety: item.variety?.commodity_variety?.[0]
                ? {
                    ...item.variety.commodity_variety[0],
                    shared_id: varietyId,
                  }
                : undefined,
              details: [],
            }
            acc.push(group)
          }

          group.details.push({
            id: item.id,
            available_qty:
              item?.storage_picked?.[0]?.trade?.deducted_quantity_remaining,
            cost_per_unit: item.cost_per_unit,
            qty:
              type === "input" || type === "packaging"
                ? item.required_quantity
                : item.final_quantity,
            required_qty: item.required_quantity,
            used_qty: item?.storage_picked?.[0]?.used_quantity,
            weight_loss_qty: item?.storage_picked?.[0]?.weight_loss_quantity,
            wastage_qty: item?.storage_picked?.[0]?.wastage_loss_quantity,
            final_quantity: item.final_quantity,
            parent_trade_required_qty: isNewFinal
              ? item?.required_quantity
              : undefined,
            // attachments: item.attachment_metadata || [],
            storage_patti_refs: item.storage_picked?.[0]?.trade_id
              ? item.storage_picked?.[0]?.trade_id
              : undefined,
            attachments: item?.attachments?.map(att => {
              return { ...att.media, shared_id: att.id }
            }),
            grade: {
              ...item.grade?.variety_grade?.[0],
              shared_id: item.grade?.id,
            },
          })
          return acc
        }, []) || []
      )
    }

    if (productionTrade && !initialized) {
      const inputItems = groupItems(productionTrade.input || [], "input")
      const outputItems = groupItems(productionTrade.output || [], "output")
      const packagingItems = groupItems(
        productionTrade.packagings || [],
        "packaging"
      )

      const newValues = {
        ...state,
        expenses: normalizeExpenses(productionTrade?.expenses || []),
        officer: productionTrade?.officer?.user[0]
          ? {
              ...productionTrade?.officer?.user[0],
              shared_id: productionTrade.officer?.id,
            }
          : undefined,
        input_items: inputItems.length
          ? inputItems
          : [{ details: [{ attachments: [] }] }],
        packaging_items: packagingItems.length
          ? packagingItems
          : [{ details: [{ attachments: [] }] }],

        output_items: outputItems.length
          ? outputItems
          : [{ details: [{ attachments: [] }] }],
        buyer: productionTrade?.buyer?.user[0]
          ? {
              ...productionTrade?.buyer?.user[0],
              shared_id: productionTrade?.buyer?.id,
            }
          : undefined,
        order_trade_ref_ids: productionTrade?.order_trade_refs?.map(refs => {
          return { id: refs?.trade_id, name: refs?.trade_id }
        }),
        buyer_tnc: productionTrade?.buyer_tnc,
        start_date_time: productionTrade?.start_datetime || dayjs(),
        end_date_time: productionTrade?.end_datetime || dayjs(),
        depot: {
          ...productionTrade?.depot,
          shared_id: productionTrade?.depot?.id,
        },
      }
      form.setValues(newValues)
      runComputations(newValues)
      setInitialized(true)
    }
  }, [data?.production_trade_by_pk, initialized])

  const onSubmit = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      return toast.custom(
        <Toast
          title="Please fix validation errors"
          intent="danger"
          subtitle={Object.values(validation.errors)?.[0] as string}
        />
      )
    }

    setSubmitting(true)

    const { data: deptData } = await upsertSharedEntity({
      group_name: SharedEntityGroup.KiteDepartment,
      name: currentDept?.name as string,
      reference_id: currentDept?.id?.toString(),
    })

    const inputs: any[] = []
    state.input_items?.forEach((group: any) => {
      group.details?.forEach((detail: any) => {
        inputs.push({
          id: isFinal ? undefined : detail.id,
          commodity_id: group.commodity?.shared_id,
          variety_id: group.variety?.shared_id,
          grade_id: detail.grade?.shared_id,
          attachment_ids: detail.attachments?.map(
            (a: any) => a?.media?.id || a?.id
          ),
          storage_picked_records: [
            {
              trade_id: detail.storage_patti_refs,
              required_quantity: Number(detail.required_qty || 0),
              cost_per_unit: Number(detail.cost_per_unit || 0),
              total_cost:
                Number(detail.required_qty || 0) *
                Number(detail.cost_per_unit || 0),
              used_quantity: isFinal ? Number(detail.used_qty || 0) : undefined,
              wastage_loss_quantity: isFinal
                ? Number(detail.wastage_qty || 0)
                : undefined,
              weight_loss_quantity: isFinal
                ? Number(detail.weight_loss_qty || 0)
                : undefined,
            },
          ],
        })
      })
    })

    const outputs: any[] = []
    state.output_items?.forEach((group: any) => {
      group.details?.forEach((detail: any) => {
        outputs.push({
          id: isFinal ? undefined : detail.id,
          commodity_id: group.commodity?.shared_id,
          variety_id: group.variety?.shared_id,
          grade_id: detail.grade?.shared_id,
          attachment_ids: detail.attachments?.map(
            (a: any) => a?.media?.id || a?.id
          ),
          cost_per_unit: Number(detail.cost_per_unit || 0),
          final_quantity: Number(detail.qty || 0),
          total_cost:
            Number(detail.qty || 0) * Number(detail.cost_per_unit || 0),
        })
      })
    })

    const packaging: any[] = []
    state.packaging_items?.forEach((group: any) => {
      group.details?.forEach((detail: any) => {
        packaging.push({
          id: isFinal ? undefined : detail.id,
          commodity_id: group.commodity?.shared_id,
          variety_id: group.variety?.shared_id,
          grade_id: detail.grade?.shared_id,
          attachment_ids: detail.attachments?.map(
            (a: any) => a?.media?.id || a?.id
          ),
          storage_picked_records: [
            {
              trade_id: detail.storage_patti_refs,
              required_quantity: Number(detail.required_qty || 0),
              cost_per_unit: Number(detail.cost_per_unit || 0),
              total_cost:
                Number(detail.required_qty || 0) *
                Number(detail.cost_per_unit || 0),
              used_quantity: isFinal ? Number(detail.used_qty || 0) : undefined,
              wastage_loss_quantity: isFinal
                ? Number(detail.wastage_qty || 0)
                : undefined,
              weight_loss_quantity: isFinal
                ? Number(detail.weight_loss_qty || 0)
                : undefined,
            },
          ],
        })
      })
    })
    setSubmitting(false)

    const commonPayload = {
      id: id !== "new" && id ? id : undefined,
      mode: state?.mode,
      start_datetime: state.start_date_time,
      department_id: deptData?.insert_setu_trade_shared_entity_one?.id,
      officer_id: state.officer?.shared_id,
      buyer_id: state.buyer?.shared_id,
      depot_id: state.depot?.shared_id,
      inputs: inputs,
      outputs: outputs,
      packaging: packaging,
      buyer_tnc: state.buyer_tnc,
      facilitator_id: state.facilitator?.shared_id,
      order_trade_ref_ids: state.order_trade_ref_ids?.map((p: any) => p.id),
      expenses: state?.expenses?.filter(
        i => i.seller || i.buyer || i.facilitator
      )?.length
        ? state?.expenses?.reduce((prev, expense) => {
            const commonProperties = {
              expense_parameter_id: expense.expense_parameter?.id,
              kind: expense.kind?.id,
            }
            if (expense.buyer?.value) {
              prev.push({
                ...commonProperties,
                ...(id !== "new" && {
                  id: expense.buyer.id,
                }),
                paid_by: "buyer",
                value: expense.buyer.value,
              })
            }
            if (expense.seller?.value) {
              prev.push({
                ...commonProperties,
                ...(id !== "new" && {
                  id: expense.seller.id,
                }),
                paid_by: "seller",
                value: expense.seller.value,
              })
            }
            if (expense.facilitator?.value) {
              prev.push({
                ...commonProperties,
                ...(id !== "new" && {
                  id: expense.facilitator.id,
                }),
                paid_by: "facilitator",
                value: expense.facilitator.value,
              })
            }
            return prev
          }, [])
        : undefined,
    }

    let error
    if (isFinal) {
      const { error: err } = await addUpdateFinalPatti({
        input: {
          ...commonPayload,
          id: isNewFinal ? undefined : id,
          parent_trade_id: id as any,
          end_datetime: state.end_date_time,
        },
      })
      error = err
    } else {
      const { error: err } = await addUpdateProductionPlanning({
        input: {
          ...commonPayload,
        },
      })
      error = err
    }
    setSubmitting(false)
    if (error) {
      return toast.custom(
        <Toast
          title="Error creating production planning"
          intent="danger"
          subtitle={error.message}
        />
      )
    } else {
      toast.custom(<Toast title={"Production Planning saved successfully!"} />)
      isFinal
        ? navigate(`/d/production-planning/final`)
        : navigate("/d/production-planning")
    }
  }

  function inputProps(name: string, sharedEntity?: SharedEntity) {
    return {
      value: get(state, name, ""),
      error: get(form.errors, name, "") as any,
      disabled: isCompletelyDisabled,
      onChange: async (e: any) => {
        const stateCopy = cloneDeep(state)
        let facilitator = stateCopy.seller?.companies?.[0]?.company
        if (name === "mode" && e === "facilitator" && facilitator) {
          const { data: facilitatorShared } = await upsertSharedEntity({
            reference_id: facilitator.codename,
            group_name: SharedEntityGroup.KiteCompany,
            name: facilitator.name,
          })
          let shared_id =
            facilitatorShared?.insert_setu_trade_shared_entity_one?.id
          set(stateCopy, "facilitator", { ...facilitator, shared_id })
        } else if (
          (name === "mode" && e === "direct") ||
          (name === "mode" && e === "facilitator" && !facilitator) ||
          (name === "seller" && !e?.companies.length)
        ) {
          set(stateCopy, "facilitator", null)
          set(stateCopy, "mode", "direct")
        }

        if (sharedEntity && e?.codename && e?.name && !e?.shared_id) {
          const { data } = await upsertSharedEntity({
            reference_id: e.codename,
            group_name: sharedEntity.group,
            name: e.name,
          })
          set(stateCopy, name, {
            ...e,
            shared_id: data?.insert_setu_trade_shared_entity_one?.id,
          })
        } else {
          set(stateCopy, name, e?.target ? e.target.value : e)
        }
        runComputations(stateCopy)
        setState(stateCopy)
      },
    }
  }
  const [removedExpenseItems, setRemovedExpenseItems] = useState<
    Record<string, any>
  >({})

  return (
    <>
      <SpinnerOverlay show={detailFetching || submitting} />
      <header className="bg-gray-100 shadow-sm p-3 border-b-gray-300 border-b flex justify-between">
        <Breadcrumbs
          items={[
            {
              text: `Production ${isFinal ? "Patti" : "Planning"}`,
              link: "/d/production-planning",
              isActive: false,
            },
            {
              text: id?.toString() || "New",
              link: `/d/production-planning/${id}`,
              isActive: true,
            },
          ]}
          linkRenderer={(link, item) => {
            return <Link to={link as any}>{item}</Link>
          }}
        />
        <Link
          to={`?timeline=${id}`}
          className="text-blue-500 underline text-sm"
        >
          Timeline
        </Link>
      </header>

      <Group className="flex flex-col gap-4 px-2 md:px-5 pt-8 pb-20 relative bg-gray-50 min-h-screen">
        <div className="w-full md:w-[85%] mx-auto space-y-6">
          {isCancelled && (
            <Toast
              title="Cancel Production"
              intent="danger"
              variant="minimal"
              className="!max-w-full"
            />
          )}

          <WhoIsTrading
            form={form}
            inputProps={inputProps}
            showSeller={false}
            requiredBuyer={false}
            disableBuyer={isCompletelyDisabled}
          />

          <HowIsItTraded
            form={form}
            type={isFinal ? "Production Patti" : "Production Planning Patti"}
            inputProps={inputProps}
            showBookingKinds
            useBuyerFacilitatorCompanies
            hideMaterialKind
          />

          <WhoOverseesProduction inputProps={inputProps} isFinal={isFinal} />

          <WhenIsItProduction
            form={form}
            inputProps={inputProps}
            isFinal={isFinal}
          />

          <ProductionParticulars
            form={form}
            inputProps={inputProps}
            runComputations={runComputations}
            isFinal={isFinal}
            completelyDisabled={isCompletelyDisabled}
            canEditFinalPatti={canEditFinalPatti}
          />

          <OrderPattiRefs form={form} inputProps={inputProps} />

          <TradeExpenses
            form={form}
            inputProps={inputProps}
            sharedDeptId={"sharedDeptId"}
            isDisabled={isCompletelyDisabled}
            advancePaymentDisabled
            onExpenseTemplateChange={expenseConfig => {
              const stateCopy = cloneDeep(form.values)
              for (const expense of stateCopy.expenses) {
                setRemovedExpenseItems(prev => {
                  if (expense?.buyer?.id) {
                    prev[expense.buyer.id] = true
                  }
                  if (expense?.seller?.id) {
                    prev[expense.seller.id] = true
                  }
                  return prev
                })
              }
              stateCopy.expenses = normalizeExpenses(
                expenseConfig.expense_configs || [],
                true
              )
              form.setValues(stateCopy)
              runComputations(stateCopy)
            }}
            onDeleteExpense={(idx, expense) => {
              const stateCopy = cloneDeep(form.values)
              stateCopy.expenses.splice(idx, 1)

              setRemovedExpenseItems(prev => {
                if (expense?.buyer?.id) {
                  prev[expense.buyer.id] = true
                }
                if (expense?.seller?.id) {
                  prev[expense.seller.id] = true
                }
                if (expense?.facilitator?.id) {
                  prev[expense.facilitator.id] = true
                }
                return prev
              })
              form.setValues(stateCopy)
              runComputations(stateCopy)
            }}
            onDeleteAdvancePayment={idx => {
              const stateCopy = cloneDeep(form.values)
              stateCopy.advance_payment.splice(idx, 1)
              form.setValues(stateCopy)
              runComputations(stateCopy)
            }}
          />

          <FormCard title="Payment">
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-700">Final Total</span>
              <span className="font-700">
                {inr(
                  (form.values.total_amount || 0) -
                    (form.values.amount_deducted || 0)
                )}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-green-600 font-500 text-xl">
                Buyer Total Amount
              </span>
              <span className="text-xl font-700 text-gray-800">
                {inr(form.values.buyer_amount || 0)}
              </span>
            </div>
          </FormCard>

          <TermsAndConditions
            kind={form.values.kind}
            mode={form.values.mode}
            sharedDeptId={"sharedDeptId"}
            seller_tnc={form.values.seller_tnc}
            buyer_tnc={form.values.buyer_tnc}
            onChange={(path, value) => form.setFieldValue(path, value)}
            isDisabled={isCompletelyDisabled}
          />
        </div>
      </Group>

      <footer className="fixed bottom-0 bg-white shadow-overlay p-4 w-[calc(100%-64px)] rounded-t-2xl text-right border-t border-gray-200 z-10">
        <div className="flex justify-end gap-3 px-10">
          <Button
            intent="primary"
            text="Send for Approval"
            leftIcon={<CheckCircle weight="fill" />}
            onClick={onSubmit}
            loading={submitting}
            disabled={isCompletelyDisabled}
          />
        </div>
      </footer>
    </>
  )
}

export default ProductionPlanningDetail
